import { Link } from "react-router-dom";
import { ArrowRight, BookOpenCheck, FileSearch, Handshake, ShieldCheck } from "lucide-react";
import { SectionTag } from "@/components/Motion";
import PageSeo from "@/components/PageSeo";
import { COMPANY } from "@/data/company";
import { hy } from "@/lib/hyphenate";

const METHOD = [
  {
    icon: FileSearch,
    title: "Une demande étudiée, pas un simple formulaire",
    text: "Vous décrivez votre métier, votre chiffre d’affaires, votre expérience et votre situation d’assurance. Un conseiller spécialiste du bâtiment vous recontacte pour préciser les activités réellement exercées et les pièces nécessaires, puis votre dossier est étudié. La demande est gratuite et sans engagement.",
  },
  {
    icon: ShieldCheck,
    title: "Des activités décrites avec précision",
    text: "Une décennale ne couvre que les activités déclarées. Nous insistons donc sur le descriptif de vos travaux, vos techniques et vos activités secondaires : c’est ce qui détermine à la fois la couverture de vos chantiers et le prix.",
  },
  {
    icon: Handshake,
    title: "Des prix annoncés honnêtement",
    text: "Les fourchettes publiées pour chaque métier sont des repères indicatifs communiqués par le cabinet, jamais des tarifs garantis. Seule l’étude de votre dossier par un assureur permet d’obtenir un prix réel.",
  },
  {
    icon: BookOpenCheck,
    title: "Des guides sourcés et datés",
    text: "Nos guides s’appuient sur les textes officiels — Code des assurances, Code civil, Service Public, décisions de la Cour de cassation — cités en fin de page. Chaque guide indique la date de sa dernière mise à jour.",
  },
];

const COMMITMENTS = [
  "Aucun faux avis, aucun partenaire ou label inventé.",
  "Les assureurs cités dans notre comparatif le sont à titre documentaire, par ordre alphabétique.",
  "Vos données servent à étudier votre demande : voir notre politique de confidentialité.",
  "Une information inexacte ? Signalez-la via le formulaire de contact pour correction.",
];

export default function About() {
  return (
    <div data-testid="about-page" className="mx-auto max-w-[1200px] px-5 sm:px-8 py-16 lg:py-24">
      <PageSeo path="/a-propos" />
      <SectionTag className="mb-4">À propos</SectionTag>
      <h1 className="font-display text-4xl sm:text-6xl font-extrabold uppercase tracking-tighter leading-[0.95] mb-8 max-w-4xl">
        {hy("Décennale Artisans : l’assurance des artisans du")} <span className="text-accent">bâtiment.</span>
      </h1>
      <p className="max-w-3xl text-base sm:text-lg leading-relaxed text-ink/80">
        {COMPANY.brand} aide les artisans et entreprises du BTP à s’assurer en RC Pro et en décennale :
        22 métiers, de la maçonnerie à l’agencement, y compris les entreprises en création. Le site publie
        des prix indicatifs par métier et des guides pratiques pour comprendre ses obligations avant de
        demander un devis.
      </p>
      {COMPANY.legalName && (
        <p className="mt-4 max-w-3xl text-sm leading-relaxed text-ink/70">
          Le site est édité par {COMPANY.legalName}
          {COMPANY.orias ? `, immatriculée à l’ORIAS sous le n° ${COMPANY.orias}` : ""}. Détails dans les{" "}
          <Link to="/mentions-legales" className="underline underline-offset-4 hover:text-accent">mentions légales</Link>.
        </p>
      )}

      <section className="mt-16" aria-labelledby="methode-title">
        <h2 id="methode-title" className="font-display text-3xl font-bold uppercase tracking-tighter mb-8">Notre méthode</h2>
        <div className="grid gap-5 md:grid-cols-2">
          {METHOD.map(({ icon: Icon, title, text }) => (
            <article key={title} className="border-2 border-ink bg-white p-6 sm:p-8">
              <Icon className="h-6 w-6 text-accent mb-4" aria-hidden="true" />
              <h3 className="font-display text-xl font-bold mb-3">{title}</h3>
              <p className="text-sm leading-relaxed text-ink/80">{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="mt-16 max-w-3xl" aria-labelledby="engagements-title">
        <h2 id="engagements-title" className="font-display text-3xl font-bold uppercase tracking-tighter mb-6">Nos engagements</h2>
        <ul className="list-disc pl-6 space-y-3 leading-relaxed text-ink/80">
          {COMMITMENTS.map((item) => <li key={item}>{item}</li>)}
        </ul>
        <p className="mt-6 text-sm leading-relaxed text-ink/75">
          Informations réglementaires (intermédiation, réclamations, médiation) :{" "}
          <Link to="/informations-reglementaires" className="underline underline-offset-4 hover:text-accent">page dédiée</Link>.
        </p>
      </section>

      <section className="mt-16 flex flex-wrap items-center justify-between gap-6 border-2 border-ink bg-accent text-paper p-8 sm:p-12">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase tracking-tighter">Une question, un devis ?</h2>
          <p className="mt-2 text-sm text-paper max-w-xl">Décrivez votre activité en 2 minutes ou écrivez-nous.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/devis" className="inline-flex items-center gap-2 border-2 border-night bg-night px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-paper transition-colors hover:bg-transparent hover:text-night">
            Demander un devis
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/contact" className="inline-flex items-center gap-2 border-2 border-paper px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] transition-colors hover:bg-paper hover:text-accent">
            Nous écrire
          </Link>
        </div>
      </section>
    </div>
  );
}
